/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { If, Then, Else } from "react-if";
import {
  Box,
  FlexContainer,
  Spacer,
  theme,
  Text,
  SelectButton,
} from "@nordcloud/gnui";
import { useQueryState } from "~/hooks";
import { StyledMultiSelect } from "~/views/events/components/DateRangeSelector/styles";
import {
  ResourceDetailsActionsQuery,
  ActionTiming,
  actionTimingOptions,
} from "../../types/ResourceDetailsActionsQuery";
import { ResourceDetailsActionsTable } from "./ResourceDetailsActionsTable";
import { ResourceDetailsActionsTableHeader } from "./ResourceDetailsActionsTableHeader";
import { ResourceDetailsIncomingActionsTable } from "./ResourceDetailsIncomingActionsTable";
import { ResourceDetailsIncomingActionsTableHeader } from "./ResourceDetailsIncomingActionsTableHeader";

type Props = {
  resourceId: string;
};

export function ResourceDetailsActionsTab({ resourceId }: Props) {
  const { queryState, updateQueryState } =
    useQueryState<ResourceDetailsActionsQuery>();
  const [resultsCount, setResultsCount] = useState(0);

  const handleActionTimingClick = (type: ActionTiming) => {
    updateQueryState({
      ...queryState,
      actionTiming: type,
      page: "1",
      limit: "20",
      // reset sorting to be safe when querying different types of query
      field: undefined,
      order: undefined,
    });
  };

  useEffect(() => {
    if (!queryState.actionTiming) {
      updateQueryState(
        {
          ...queryState,
          actionTiming: ActionTiming.pastCurrent,
        },
        "replace"
      );
    }
  }, [queryState, updateQueryState]);

  return (
    <>
      <Box boxStyle="lightGrey" spacing="spacing04">
        <FlexContainer justifyContent="space-between">
          <Text tag="span" mr={theme.spacing.spacing04}>
            Show:
          </Text>
          <StyledMultiSelect size="small">
            {actionTimingOptions.map((type) => {
              return (
                <SelectButton
                  key={type.value}
                  name={type.value}
                  value={type.value}
                  labelText={type.label}
                  isActive={queryState.actionTiming === type.value}
                  onClick={() => handleActionTimingClick(type.value)}
                />
              );
            })}
          </StyledMultiSelect>
          <Spacer width={theme.spacing.spacing08} />
          <If condition={queryState.actionTiming === ActionTiming.pastCurrent}>
            <Then>
              <ResourceDetailsActionsTableHeader resultsCount={resultsCount} />
            </Then>
            <Else>
              <ResourceDetailsIncomingActionsTableHeader
                resultsCount={resultsCount}
              />
            </Else>
          </If>
        </FlexContainer>
      </Box>
      <If condition={queryState.actionTiming === ActionTiming.pastCurrent}>
        <Then>
          <ResourceDetailsActionsTable
            resourceId={resourceId}
            onCountChange={setResultsCount}
          />
        </Then>
        <Else>
          <ResourceDetailsIncomingActionsTable
            resourceId={resourceId}
            onCountChange={setResultsCount}
          />
        </Else>
      </If>
    </>
  );
}
